import React from 'react'
import { observer } from 'mobx-react-lite'

import Stack from '@mui/material/Stack'

import { useFinstrongStore, useGlobalCopyStore } from '../../common'
import { KeyIndicatorsTabItemType } from '../constants'
import { getDaysOfCashOnHandState, getEmergencySavingsState } from '../util/finstrongUtils'

import KeyIndicatorsTabsItem from './KeyIndicatorsTabItem'
import { formatCurrency } from '../../core/utils/NumberFormatting'
import { buildDynamicCopy } from '../../core/utils/Localization'

const KeyIndicatorsSaveTab: React.FC = () => {
  const { healthScore } = useFinstrongStore()
  const { finstrong: copyContent } = useGlobalCopyStore()
  const { key_indicators_save: saveCopy } = copyContent

  const getDaysOfCashOnHandStatus = (days: number | null) => {
    const state = getDaysOfCashOnHandState(days)

    const cardStatusMapping = {
      GOOD: saveCopy.days_of_cash_good_description,
      FAIR: saveCopy.days_of_cash_fair_description,
      OFF_TRACK: saveCopy.days_of_cash_off_track_description,
      MISSING: saveCopy.days_of_cash_missing_description,
    }

    const description =
      state === 'MISSING'
        ? cardStatusMapping[state]
        : (buildDynamicCopy(cardStatusMapping[state], days) as string)

    return {
      cardState: state,
      description,
      title: saveCopy.days_of_cash_title,
    }
  }

  const getEmergencySavingsStatus = (dollars: number | null) => {
    const state = getEmergencySavingsState(dollars)

    const cardStatusMapping = {
      GOOD: saveCopy.emergency_savings_good_description,
      FAIR: saveCopy.emergency_savings_fair_description,
      OFF_TRACK: saveCopy.emergency_savings_off_track_description,
      MISSING: saveCopy.emergency_savings_missing_description,
    }

    let description: string

    if (state === 'FAIR' && dollars !== null) {
      description = buildDynamicCopy(
        cardStatusMapping[state],
        formatCurrency(dollars, '0,0.00'),
      ) as string
    } else {
      description = cardStatusMapping[state]
    }

    return {
      cardState: state,
      description,
      title: saveCopy.emergency_savings_title,
    }
  }

  const { daysOfCashOnHand, emergencySavings } = React.useMemo(() => {
    const noOfDays =
      healthScore.months_of_cash_on_hand !== null
        ? Math.round(healthScore.months_of_cash_on_hand * 30)
        : null
    const daysOfCashOnHand = getDaysOfCashOnHandStatus(noOfDays)

    const noOfDollars =
      healthScore.emergency_savings_on_hand !== null
        ? Math.round(healthScore.emergency_savings_on_hand)
        : null
    const emergencySavings = getEmergencySavingsStatus(noOfDollars)

    return { daysOfCashOnHand, emergencySavings }
  }, [
    healthScore.months_of_cash_on_hand,
    healthScore.emergency_savings_on_hand,
    saveCopy,
    copyContent,
  ])

  return (
    <Stack direction="column" gap={24} sx={{ p: 8, pt: 24 }}>
      <KeyIndicatorsTabsItem
        cardState={daysOfCashOnHand.cardState}
        description={daysOfCashOnHand.description}
        drawerFor={KeyIndicatorsTabItemType.SaveEnoughToLiveOn}
        title={daysOfCashOnHand.title}
      />
      <KeyIndicatorsTabsItem
        cardState={emergencySavings.cardState}
        description={emergencySavings.description}
        drawerFor={KeyIndicatorsTabItemType.MaintainEmergencySavings}
        title={emergencySavings.title}
      />
    </Stack>
  )
}

export default observer(KeyIndicatorsSaveTab)
